import { showToast } from 'redux/modules/toasts.actions';
import type { IState } from 'redux/state';
import { timelyApi as rawTimelyApi } from './GENERATED_ARTIFACT';

// Lazy import to break the circular dependency: `util.optimisticUpdates` imports
// `timelyApi` back (for `updateProposalCache`), so importing it eagerly here
// would create a cycle.
const getOptimisticUpdates = () =>
  import('~domains/ai/timesheet_proposals/utils/util.optimisticUpdates');

import type { PatchTimesheetProposalEntryApiArg } from './GENERATED_ARTIFACT';

// The claim path batches many PATCH mutations and invalidates exactly once at the
// end, so each patch can carry a `skipInvalidation` meta flag its onQueryStarted
// reads. The generated PATCH arg is an OBJECT (`{ entryId, body }`), so extending
// it with `meta` is type-clean.
//
// NOTE on DELETE: the Rails (timely) artifact types delete's arg as a BARE STRING
// (entry id) — `flattenArg` collapsed the single path param. It therefore can't
// carry a meta flag, so delete uses no skip mechanism; instead its onQueryStarted
// only ever invalidates the collection tag (`TimesheetProposals`), never the
// single-proposal tag — so a delete inside a claim batch can't interrupt the
// flash animation (which is driven by the `TimesheetProposal`-tagged full-proposal
// cache, already kept correct by the optimistic removal).
export type PatchTimesheetProposalEntryApiArgWithMeta = PatchTimesheetProposalEntryApiArg & {
  meta?: { skipInvalidation?: boolean };
};

/**
 * Enhancement layer over the generated artifact (`./GENERATED_ARTIFACT.ts`): the
 * codegen emits the raw transport + typed endpoints/hooks, and this file layers
 * the custom cache tags + optimistic onQueryStarted on top via `enhanceEndpoints`.
 * Keeping them here — not in the generated file — means they survive every
 * regeneration.
 * @link https://redux-toolkit.js.org/rtk-query/api/created-api/code-splitting#enhanceendpoints
 */
export const timelyApi = rawTimelyApi.enhanceEndpoints({
  addTagTypes: ['TimesheetProposal', 'TimesheetProposals'] as const,

  endpoints: {
    // Timesheet Proposals
    listTimesheetProposals: {
      providesTags: ['TimesheetProposals'],
    },
    getTimesheetProposal: {
      // `flattenArg` collapses the single-parameter arg (only the proposal `id`
      // survives the account/auth strip) to a bare string, so `arg` IS the id.
      providesTags: (result, error, arg) => [
        { type: 'TimesheetProposal', id: arg },
        'TimesheetProposal',
      ],
    },
    deleteTimesheetProposal: {
      invalidatesTags: ['TimesheetProposals'],
    },
    submitTimesheetProposal: {
      invalidatesTags: ['TimesheetProposal', 'TimesheetProposals'],
    },
    unsubmitTimesheetProposal: {
      invalidatesTags: ['TimesheetProposal', 'TimesheetProposals'],
    },
    generateTimesheetProposal: {
      invalidatesTags: ['TimesheetProposals'],
    },
    // regenerateEntryNotes: no cache tags (async note regeneration; results
    // arrive via Pusher, not through cache invalidation).

    // --- Entry CRUD ---
    //
    // These patch the SAME `getTimesheetProposal` cache the display renders. The
    // cache holds RAW Rails ids (entries[].id is a NUMBER; the transformer
    // String()-coerces at READ time), so every id match here coerces the numeric
    // cache id to a string before comparing against the string entryId arg —
    // otherwise `number !== string` silently no-ops the optimistic write.
    //
    // Invalidation is conditional (skipInvalidation meta) and done imperatively
    // in onQueryStarted, so the generated static `invalidatesTags: ['AutoSheet']`
    // is neutralized to `[]` here (after the providesTags overrides above, nothing
    // provides 'AutoSheet' anyway).
    createTimesheetProposalEntry: {
      invalidatesTags: [],
      onQueryStarted: async (args, { dispatch, getState, queryFulfilled }) => {
        const state = getState() as IState;
        const cachedQueries = timelyApi.util.selectCachedArgsForQuery(
          state,
          'getTimesheetProposal'
        );

        const { createOptimisticEntry, updateProposalCache } = await getOptimisticUpdates();
        const optimisticEntry = createOptimisticEntry(args);

        // Optimistic insert; rollback is wired via updateProposalCache's own
        // queryFulfilled.catch(). The placeholder carries a string `optimistic_*`
        // id (a sentinel) into a numeric-id-typed list — cast at the push.
        //
        // Append IN-PLACE via `.push` (not a whole-array reassignment) so Immer
        // records a granular inverse patch (pop just this element on rollback),
        // symmetric with the splice-based delete above. A whole-array reassign
        // would, on a failed create rolled back concurrently with another create,
        // clobber the other in-flight optimistic entry.
        updateProposalCache(
          dispatch,
          cachedQueries,
          args.proposalId,
          draft => {
            if (!draft.proposal.entries) draft.proposal.entries = [];
            draft.proposal.entries.push(optimisticEntry as never);
          },
          queryFulfilled
        );

        try {
          const { data } = await queryFulfilled;
          // Swap the optimistic placeholder for the server entry by exact id —
          // safe when multiple concurrent creates are in flight.
          cachedQueries
            .filter(query => query === args.proposalId)
            .forEach(query => {
              dispatch(
                timelyApi.util.updateQueryData('getTimesheetProposal', query, draft => {
                  if (draft.proposal.entries && data.entry) {
                    const i = draft.proposal.entries.findIndex(
                      entry => String(entry.id) === optimisticEntry.id
                    );
                    if (i >= 0) draft.proposal.entries[i] = data.entry;
                  }
                })
              );
            });
        } catch {
          dispatch(showToast({ message: 'Failed to create entry', type: 'danger' }));
        }
      },
    },
    deleteTimesheetProposalEntry: {
      // Override the generated static `invalidatesTags: ['AutoSheet']` (orphaned
      // after the providesTags overrides above) with collection-only invalidation
      // done imperatively below. The arg is the BARE entry-id string (flattenArg).
      invalidatesTags: [],
      onQueryStarted: async (entryId, { dispatch, getState, queryFulfilled }) => {
        const state = getState() as IState;
        const cachedQueries = timelyApi.util.selectCachedArgsForQuery(
          state,
          'getTimesheetProposal'
        );

        // Optimistically drop the entry from every cached proposal. The cache
        // holds a NUMBER id; the arg is a STRING — coerce the cache id before
        // comparing, else `number !== string` would never match.
        //
        // CONCURRENCY: the batch-delete path (useEntryActions) fires many
        // deletes at once (Promise.allSettled) against the SAME cache. Positional
        // inverse patches — whether from a whole-array `.filter` reassign OR a
        // `splice` — are NOT concurrency-safe: `patchResult.undo()` re-inserts at a
        // STALE index recorded when the patch was applied, so a failed delete whose
        // sibling sat at a LOWER index than a concurrent SUCCESSFUL delete shifts
        // (and, for multiple failures, replays a positional `replace` sequence that
        // DUPLICATES rows) — resurrecting entries another delete already removed.
        // So we DON'T roll back positionally. Instead we capture the removed entry
        // (+ its original index) from the PRE-splice cache — read it via the
        // endpoint selector (plain objects, NOT an Immer draft, so it's safe to
        // retain) — and, on rejection, re-add ONLY that entry via an ID-KEYED,
        // IDEMPOTENT patch that reads CURRENT cache state. It never touches a
        // sibling, so the final cache is always exactly the original set minus the
        // successfully-deleted ids, in EVERY interleaving.
        const removals = cachedQueries.map(query => {
          // Snapshot from the live (non-draft) cache BEFORE the optimistic splice.
          const before =
            timelyApi.endpoints.getTimesheetProposal.select(query)(state).data?.proposal.entries ??
            [];
          const index = before.findIndex(entry => String(entry.id) === entryId);
          const removed = index >= 0 ? { entry: before[index], index } : undefined;

          dispatch(
            timelyApi.util.updateQueryData('getTimesheetProposal', query, draft => {
              const entries = draft.proposal.entries;
              if (!entries) return;
              const i = entries.findIndex(entry => String(entry.id) === entryId);
              if (i >= 0) entries.splice(i, 1);
            })
          );
          return { query, removed };
        });

        try {
          await queryFulfilled;
          // Collection-only: refresh proposal-list summaries. Never the
          // single-proposal tag — the optimistic removal already brought the
          // full-proposal cache to its post-delete state, and refetching it would
          // interrupt the claim flash animation.
          dispatch(timelyApi.util.invalidateTags(['TimesheetProposals']));
        } catch (_error) {
          // ID-keyed idempotent rollback: re-add THIS entry only if it isn't
          // already present (idempotent), clamping its original index to the
          // current length. Reads live cache state, so any concurrent successful
          // delete that already removed a sibling stays removed — no resurrection
          // in any ordering.
          for (const { query, removed } of removals) {
            if (!removed) continue;
            const captured = removed;
            dispatch(
              timelyApi.util.updateQueryData('getTimesheetProposal', query, draft => {
                const entries = draft.proposal.entries;
                if (!entries) return;
                if (entries.some(entry => String(entry.id) === entryId)) return;
                const at = Math.min(captured.index, entries.length);
                entries.splice(at, 0, captured.entry as never);
              })
            );
          }
          dispatch(showToast({ message: 'Failed to delete entry', type: 'danger' }));
        }
      },
    },
    patchTimesheetProposalEntry: {
      invalidatesTags: [],
      onQueryStarted: async (args, { dispatch, getState, queryFulfilled }) => {
        // Track all cache patches for rollback on failure.
        const patchResults: { undo: () => void }[] = [];

        try {
          const state = getState() as IState;
          const cachedQueries = timelyApi.util.selectCachedArgsForQuery(
            state,
            'getTimesheetProposal'
          );

          for (const query of cachedQueries) {
            try {
              const proposalPatch = dispatch(
                timelyApi.util.updateQueryData('getTimesheetProposal', query, draft => {
                  if (!draft.proposal.entries) return;

                  const sourceEntry = draft.proposal.entries.find(
                    e => String(e.id) === args.entryId
                  );
                  if (!sourceEntry) return;

                  Object.assign(sourceEntry, args.body);
                })
              );
              patchResults.push(proposalPatch);
            } catch (error) {
              console.warn('Failed to update cached proposal for patchEntry:', query, error);
            }
          }

          await queryFulfilled;

          // Skip invalidation if skipInvalidation meta flag is set (bulk path).
          const argsWithMeta = args as PatchTimesheetProposalEntryApiArgWithMeta;
          if (!argsWithMeta.meta?.skipInvalidation) {
            dispatch(timelyApi.util.invalidateTags(['TimesheetProposal', 'TimesheetProposals']));
          }
        } catch (error) {
          // Rollback optimistic updates on failure.
          patchResults.forEach(patch => patch.undo());
          throw error;
        }
      },
    },
  },
});
